import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spin, Tooltip, message } from "antd";
import { DownloadOutlined, FilePdfOutlined, PrinterOutlined, QrcodeOutlined } from "@ant-design/icons";
import { eventUseMutation } from "../../hooks";
import {
    downloadCheckQrPdf,
    downloadCheckQrPng,
    getCheckQrCoordinatesKey,
    getCheckQrStorageKey,
    printCheckQr,
    readCheckQrRevision,
    removeCheckQrRevision,
    withCheckQrRevision,
    writeCheckQrRevision,
} from "../../helpers/event-check-qr-helper";
import {
    hasMeaningfulCheckCoordinates,
    isValidCheckRadius,
} from "../../helpers/event-check-location-helper";
import Icons from "components/icons";
import { Localize } from "components/service";
import useCheckInPositionDate from "../../hooks/use-check-in-position-date";
import useCheckQrPosterExport from "../../hooks/use-check-qr-poster-export";
import { ENABLE_QR_OUTDATED_CHECK } from "../../helpers/event-check-qr-policy";
import useCheckQrRegenerateConfirmation from "../../hooks/use-check-qr-regenerate-confirmation";
import CheckQrRegenerateModal from "../../fields/check-qr-regenerate-modal";


const EventCheckQrGenerate = ({
    event,
    eventID,
    icon,
    label,
    modalTitle = false,
    variant = "default",
    qrStatus,
    qrUrl: controlledQrUrl,
    onGenerateQr,
    positionDate: sharedPositionDate,
    disabled = false,
    buttonClassName = "",
}) => {
    const initialQrUrl = event?.getCheckQrCodeUrl;
    const [ modalOpen, setModalOpen ] = useState(false);
    const [ qrUrl, setQrUrl ] = useState(() => withCheckQrRevision(
        initialQrUrl,
        event?.check_qr_generated_at || readCheckQrRevision(eventID)
    ));
    const [ qrGeneratedAt, setQrGeneratedAt ] = useState(event?.check_qr_generated_at ?? null);
    const eventContext = useMemo(() => ({ eventID }), [ eventID ]);
    const activeEventRef = useRef(eventContext);
    const mountedRef = useRef(true);

    activeEventRef.current = eventContext;
    const localPositionDate = useCheckInPositionDate({
        eventID,
        enabled: !sharedPositionDate && modalOpen,
    });
    const positionDate = sharedPositionDate || localPositionDate;

    const staffManagementVariant = variant === "staff-management";
    const controlled = Boolean(qrStatus);
    const canGenerate = (
        hasMeaningfulCheckCoordinates(event?.check_latitude, event?.check_longitude) &&
        (!staffManagementVariant || isValidCheckRadius(event?.check_radius))
    );
    const modalTitleID = `event-check-qr-modal-title-${ eventID }`;
    const currentCoordinatesKey = useMemo(() => getCheckQrCoordinatesKey(event), [ event ]);
    const qrStorageKey = useMemo(() => getCheckQrStorageKey(eventID), [ eventID ]);
    const hasQrUrl = !!qrUrl;
    let storedCoordinatesKey = null;

    if (!controlled)
    {
        try
        {
            storedCoordinatesKey = window.localStorage.getItem(qrStorageKey);
        } catch (error)
        {
            storedCoordinatesKey = null;
        }
    }
    const needsQrRefresh = ENABLE_QR_OUTDATED_CHECK && !controlled && hasQrUrl &&
        storedCoordinatesKey && storedCoordinatesKey !== currentCoordinatesKey;
    const controlledLoading = [ "generating", "regenerating" ].includes(qrStatus);
    const controlledReady = qrStatus === "ready";
    const canView = hasQrUrl && !needsQrRefresh && (!controlled || controlledReady);
    const fillCoordinatesLabel = Localize({
        children: "CHECK_QR.Tooltip_FillCoordinates",
    }).props.children;
    const storeCurrentCoordinatesKey = () => {
        try
        {
            window.localStorage.setItem(qrStorageKey, currentCoordinatesKey);
        } catch (error)
        {
            // Opening or generating the QR must not depend on browser storage availability.
        }
    };

    useEffect(() => {
        mountedRef.current = true;

        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        setModalOpen(false);
    }, [ eventContext ]);

    useEffect(() => {
        setQrUrl(controlled ?
            controlledQrUrl :
            withCheckQrRevision(
                event?.getCheckQrCodeUrl,
                event?.check_qr_generated_at || readCheckQrRevision(eventID)
            )
        );
        setQrGeneratedAt(event?.check_qr_generated_at ?? null);
    }, [ controlled, controlledQrUrl, event?.getCheckQrCodeUrl, event?.check_qr_generated_at, eventID ]);

    useEffect(() => {
        if (controlled && !controlledReady)
        {
            setModalOpen(false);
        }
    }, [ controlled, controlledReady ]);

    const {
        _setEventCheckQrGenerate,
        loadingMutationEventCheckQrGenerate,
    } = eventUseMutation.checkQrGenerate();
    const generateQr = async () => {
        const isCurrent = () => mountedRef.current && activeEventRef.current === eventContext;

        try
        {
            if (controlled)
            {
                const nextQrUrl = await onGenerateQr();

                if (!isCurrent()) return null;

                if (nextQrUrl)
                {
                    setQrUrl(nextQrUrl);
                    setModalOpen(true);
                }

                return nextQrUrl;
            }

            const result = await _setEventCheckQrGenerate({ variables: { ee_id: eventID } });

            if (!isCurrent() || result?.errors?.length) return null;

            const generatedEvent = result?.data?.eeCheckQrGenerate?.exhibitionEvent;
            const nextQrUrl = generatedEvent?.getCheckQrCodeUrl;

            if (!nextQrUrl)
            {
                message.error(<Localize>CHECK_QR.Text_NoQrUrl</Localize>);
                return null;
            }

            const previousQrUrl = event?.getCheckQrCodeUrl;
            const nextGeneratedAt = generatedEvent?.check_qr_generated_at ?? null;
            const nextRevision = nextGeneratedAt || (previousQrUrl && nextQrUrl === previousQrUrl ?
                String(Date.now()) :
                null);

            if (nextRevision)
            {
                writeCheckQrRevision(eventID, nextRevision);
            } else
            {
                removeCheckQrRevision(eventID);
            }

            setQrUrl(withCheckQrRevision(nextQrUrl, nextRevision));
            setQrGeneratedAt(nextGeneratedAt);
            storeCurrentCoordinatesKey();
            setModalOpen(true);
            return nextQrUrl;
        } catch (error)
        {
            // The mutation hook reports errors; leave the existing QR available for another attempt.
            return null;
        }
    };
    const generationDisabled = disabled || !canGenerate || (controlled ?
        controlledLoading || !onGenerateQr : loadingMutationEventCheckQrGenerate);
    const { requestGeneration, confirmationProps } = useCheckQrRegenerateConfirmation({
        eventID,
        qrUrl,
        disabled: generationDisabled,
        onGenerate: generateQr,
    });

    const openQr = () => {
        if (disabled) return;

        if (canView)
        {
            if (!controlled && !storedCoordinatesKey)
            {
                storeCurrentCoordinatesKey();
            }

            setModalOpen(true);
            return;
        }

        return requestGeneration();
    };

    const controlledButtonLabel = [ "outdated", "regenerating" ].includes(qrStatus) ?
        <Localize>CHECK_QR.Button_Regenerate</Localize> :
        [ "generation_failed", "regeneration_failed" ].includes(qrStatus) ?
            <Localize>CHECK_QR.Button_Retry</Localize> :
            controlledReady ?
                <Localize>CHECK_QR.Button_View</Localize> :
                <Localize>CHECK_QR.Button_Create</Localize>;
    const defaultButtonLabel = controlled ?
        controlledButtonLabel :
        hasQrUrl && !needsQrRefresh ?
            <Localize>CHECK_QR.Button_View</Localize> :
            needsQrRefresh ?
                <Localize>CHECK_QR.Button_Update</Localize> :
                <Localize>CHECK_QR.Button_Generate</Localize>;

    const { exportLoading, exportPoster, qrGeneratedAtText, positionUpdatedAtText } = useCheckQrPosterExport({
        eventID,
        qrUrl,
        qrGeneratedAt,
        eventTitle: event?.title,
        positionDate,
        disabled: disabled || !modalOpen || (controlled && !controlledReady),
    });
    const downloadPng = () => exportPoster("png", downloadCheckQrPng);
    const downloadPdf = () => exportPoster("pdf", downloadCheckQrPdf);
    const printQr = () => exportPoster("print", printCheckQr);

    const button = (
        <Button
            className={ `ant-btn-secondary event-check-qr-generate__button ${ buttonClassName }`.trim() }
            disabled={ disabled || (!canView && generationDisabled) }
            loading={ controlled ? controlledLoading : loadingMutationEventCheckQrGenerate }
            onClick={ openQr }>
            { icon || <QrcodeOutlined /> }
            <span>{ label || defaultButtonLabel }</span>
        </Button>
    );

    return (
        <>
            { canView || canGenerate ?
                button :
                <Tooltip title={ fillCoordinatesLabel }>
                    <span
                        className="event-check-qr-generate__disabled"
                        tabIndex={ 0 }
                        aria-label={ fillCoordinatesLabel }>
                        { button }
                    </span>
                </Tooltip>
            }

            <CheckQrRegenerateModal { ...confirmationProps } />

            <Modal
                className={ `modal-standard event-check-qr-modal ${ staffManagementVariant ? "event-check-qr-modal--staff-management" : "" }` }
                centered
                visible={ modalOpen }
                footer={ null }
                modalRender={ (modal) => React.cloneElement(modal, {
                    ref: (node) => node?.closest("[role='dialog']")?.setAttribute("aria-labelledby", modalTitleID),
                }) }
                width={ staffManagementVariant ? 380 : 420 }
                destroyOnClose={ true }
                onCancel={ () => setModalOpen(false) }>
                <strong id={ modalTitleID } className="modal-title">
                    { modalTitle || <Localize>CHECK_QR.Modal_Title</Localize> }
                </strong>

                <div className="event-check-qr-modal__content">
                    { qrUrl &&
                        <img
                            className="event-check-qr-modal__image"
                            src={ qrUrl }
                            alt="QR Code" />
                    }

                    { qrUrl &&
                        <p className="event-check-qr-modal__description">
                            <Localize>STAFF_MANAGEMENT.Text_QrDescription</Localize>
                        </p>
                    }

                    { qrUrl &&
                        <div className="check-qr-position-date" aria-live="polite">
                            <div>{ qrGeneratedAtText }</div>
                            <div>
                                { positionDate.loading ? <Spin size="small" /> : positionUpdatedAtText }
                            </div>
                        </div>
                    }

                    <div className="event-check-qr-modal__actions">
                        <Button
                            className={ `event-check-qr-modal__action-button event-check-qr-modal__action-button--primary${ staffManagementVariant ? " btn-icon-text" : "" }` }
                            disabled={ disabled || !!exportLoading }
                            loading={ exportLoading === "png" }
                            onClick={ downloadPng }>
                            { staffManagementVariant ? <Icons.Download /> : <DownloadOutlined /> }
                            <Localize>CHECK_QR.Button_DownloadPng</Localize>
                        </Button>
                        <Button
                            className={ `event-check-qr-modal__action-button${ staffManagementVariant ? " btn-icon-text event-check-qr-modal__action-button--primary" : "" }` }
                            disabled={ disabled || !!exportLoading }
                            onClick={ downloadPdf }
                            loading={ exportLoading === "pdf" }>
                            { staffManagementVariant ? <Icons.Download /> : <FilePdfOutlined /> }
                            <Localize>CHECK_QR.Button_DownloadPdf</Localize>
                        </Button>
                        <Button
                            className={ `event-check-qr-modal__action-button event-check-qr-modal__action-button--print${ staffManagementVariant ? " btn-icon-text" : "" }` }
                            disabled={ disabled || !!exportLoading }
                            loading={ exportLoading === "print" }
                            onClick={ printQr }>
                            { staffManagementVariant ? <Icons.StaffPrinter /> : <PrinterOutlined /> }
                            <Localize>CHECK_QR.Button_Print</Localize>
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EventCheckQrGenerate;
